import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginApi, saveAuth } from "../services/Request";
import { toast } from "react-toastify";
import {
  FaShieldAlt,
  FaEnvelope,
  FaLock,
  FaArrowRight,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!email || !password) {
      setErrorMsg("Please provide both email and password.");
      return;
    }

    try {
      setLoading(true);
      const res = await loginApi({ email: email.trim(), password });

      if (res.status && res.token) {
        saveAuth(res.token, res.user);
        toast.success(`Welcome back, ${res.user.name || "User"}!`);

        // If admin, they can access both, redirect to NIN form by default
        navigate("/dashboard");
      } else {
        setErrorMsg(res.message || "Invalid credentials.");
      }
    } catch (err) {
      setErrorMsg(
        err.message || "Login failed. Please check your credentials.",
      );
      toast.error(err.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card} className="fade-in">
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.badgeWrapper}>
            <div style={styles.shieldIcon}>
              <FaShieldAlt style={{ fontSize: "28px", color: "#10B981" }} />
            </div>
          </div>
          <h1 style={styles.title}>NIN Verification Portal</h1>
          <p style={styles.subtitle}>
            Official identity authentication & verification system
          </p>
        </div>

        {/* Error notification banner */}
        {errorMsg && (
          <div style={styles.errorBox}>
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Official Email Address</label>
            <div style={styles.inputWrapper}>
              <FaEnvelope style={styles.inputIcon} />
              <input
                type="email"
                required
                style={styles.input}
                placeholder="name@nin.gov.ng"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <label style={styles.label}>Password</label>
            </div>
            <div style={styles.inputWrapper}>
              <FaLock style={styles.inputIcon} />
              <input
                type={showPassword ? "text" : "password"}
                required
                style={{ ...styles.input, paddingRight: "44px" }}
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeBtn}
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitBtn,
              opacity: loading ? 0.75 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Authenticating..." : "Sign In to Portal"}
            {!loading && <FaArrowRight style={{ marginLeft: "8px" }} />}
          </button>
        </form>

        {/* Footer info */}
        <div style={styles.footer}>
          <span>Secured with 256-Bit SSL Encryption • NIMC Compliant</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    background: "linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)",
  },
  card: {
    width: "100%",
    maxWidth: "460px",
    backgroundColor: "#FFFFFF",
    borderRadius: "20px",
    padding: "40px 36px",
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03)",
    border: "1px solid #E2E8F0",
  },
  header: {
    textAlign: "center",
    marginBottom: "28px",
  },
  badgeWrapper: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "16px",
  },
  shieldIcon: {
    width: "60px",
    height: "60px",
    borderRadius: "16px",
    backgroundColor: "#064E3B",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 16px -2px rgba(6, 78, 59, 0.25)",
  },
  title: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#0F172A",
    letterSpacing: "-0.5px",
    margin: "0 0 6px 0",
  },
  subtitle: {
    fontSize: "13px",
    color: "#64748B",
    margin: 0,
  },
  errorBox: {
    backgroundColor: "#FEF2F2",
    border: "1px solid #FEE2E2",
    color: "#DC2626",
    fontSize: "13px",
    fontWeight: "500",
    padding: "10px 14px",
    borderRadius: "10px",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#334155",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: "14px",
    fontSize: "15px",
    color: "#94A3B8",
  },
  input: {
    width: "100%",
    padding: "12px 14px 12px 42px",
    fontSize: "14px",
    border: "1px solid #CBD5E1",
    borderRadius: "10px",
    outline: "none",
    color: "#1E293B",
    backgroundColor: "#F8FAFC",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  eyeBtn: {
    position: "absolute",
    right: "12px",
    background: "none",
    border: "none",
    color: "#94A3B8",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  submitBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "8px",
    padding: "13px",
    fontSize: "15px",
    fontWeight: "700",
    color: "#FFFFFF",
    backgroundColor: "#064E3B",
    border: "none",
    borderRadius: "10px",
    transition: "background-color 0.2s, transform 0.1s",
    boxShadow: "0 4px 6px -1px rgba(6, 78, 59, 0.2)",
  },
  demoSection: {
    marginTop: "24px",
    paddingTop: "20px",
    borderTop: "1px dashed #E2E8F0",
    textAlign: "center",
  },
  demoLabel: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    display: "block",
    marginBottom: "10px",
  },
  demoBtns: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  demoPill: {
    padding: "6px 12px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#064E3B",
    backgroundColor: "#ECFDF5",
    border: "1px solid #A7F3D0",
    borderRadius: "20px",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  footer: {
    marginTop: "24px",
    textAlign: "center",
    fontSize: "11px",
    color: "#94A3B8",
  },
};
